import { useMemo } from 'react'
import { InitiativeCard } from '@app/components/blocks/card/InitiativeCard'
import { labelAddressBuilder, type AddressModel } from '@app/util/helpers'
import { pages } from '@app/util/links'
import defaultProjectPicture from '@app/assets/defaultProjectPicture.png'
import { styled } from 'inlines'
import useMediaQuery from '@app/util/useMediaQuery'
import { getSpotlightTileLayouts } from './getSpotlightTileLayouts'

export type SpotlightInitiativeForGrid = {
  slug: string
  title: string
  description?: string | null
  searchingForMembers?: boolean
  images?: { src?: string | null }[] | null
  location: { address: AddressModel }
  phase?: { name?: string } | null
}

const spotlightGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(250px, 1fr))',
  '@media (max-width: 1200px)': {
    gridTemplateColumns: 'repeat(2, minmax(250px, 1fr))',
  },
  '@media (max-width: 800px)': {
    gridTemplateColumns: 'repeat(1, minmax(250px, 1fr))',
  },
  columnGap: '1.5rem',
  rowGap: '2rem',
} as const

export type SpotlightInitiativeGridProps = {
  initiatives: readonly SpotlightInitiativeForGrid[]
  notSpecified: string
  searchingForMembersLabel: string
  notSearchingForMembersLabel: string
}

export const SpotlightInitiativeGrid = ({
  initiatives,
  notSpecified,
  searchingForMembersLabel,
  notSearchingForMembersLabel,
}: SpotlightInitiativeGridProps) => {
  const isMobile = useMediaQuery()
  const layouts = useMemo(
    () => getSpotlightTileLayouts(initiatives.length, isMobile),
    [initiatives.length, isMobile],
  )

  return (
    <styled.div style={spotlightGridStyle}>
      {initiatives.map((initiative, index) => {
        const { isSpotlight, gridColumn } = layouts[index] ?? {
          isSpotlight: false,
          gridColumn: null,
        }
        return (
          <InitiativeCard
            key={initiative.slug}
            variant={isSpotlight ? 'spotlight' : 'default'}
            style={{ gridColumn }}
            to={pages.initiative(initiative.slug)}
            image={initiative.images?.[0]?.src ?? defaultProjectPicture}
            title={initiative.title}
            description={isSpotlight ? initiative.description : null}
            location={labelAddressBuilder(
              initiative.location.address,
              notSpecified,
            )}
            phase={initiative.phase?.name}
            tagText={
              initiative.searchingForMembers
                ? searchingForMembersLabel
                : notSearchingForMembersLabel
            }
            tagVariant={initiative.searchingForMembers ? 'success' : 'neutral'}
          />
        )
      })}
    </styled.div>
  )
}
